import React, { useCallback, useEffect, useRef, useState } from 'react'
import { keyframes, styled } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { BsFillCameraVideoFill } from 'react-icons/bs'

import { backgroundProblemTooltip } from '../../../services/backgroundProblemCopy'
import type { BackgroundProblem } from '../../../services/liveKitVideoBackground'
import { whenVideoPainted } from '../../../utils/whenVideoPainted'

/**
 * SC-158: a camera that is on but held for its background — ours, or someone
 * else's as they told us (F8). Not a camera turned off.
 */
export const CAMERA_WAITING_LABEL = 'Camera on, waiting for background'

/**
 * What our waiting cover, sidebar badge and nameplate say, so the three never
 * disagree: what is wrong when a problem keeps the camera down (only we know
 * that), otherwise that it waits.
 */
export function cameraWaitingLabel(problem: BackgroundProblem | null): string {
  return problem ? backgroundProblemTooltip(problem) : CAMERA_WAITING_LABEL
}

export const CAMERA_WAITING_COLOR = 'var(--sevv-warning, #f59e0b)'

/**
 * The dark box of a tile without a picture: waiting, off or absent alike, so a
 * waiting cover never looks different from the camera-off box beside it. Not
 * `--sevv-overlay-backdrop`, the lighter modal scrim. The theme sets
 * `--sevv-video-tile-bg` from `chrome.videoTileBackground`
 * (`applySevvCssVariables`); the fallback only shows before a theme is applied.
 * The variable is the one place a theme can restyle every tile and the
 * picture-in-picture window together (`pipStyles` uses the same expression).
 */
export const VIDEO_TILE_COVER_BACKGROUND = 'var(--sevv-video-tile-bg, rgba(0, 0, 0, 0.8))'

/**
 * Someone else's camera that has waited this long is not about to come up.
 * Its tile stops pulsing rather than draw the eye for as long as it lasts; we
 * cannot know why it waits (F8 says only that it does).
 */
export const REMOTE_WAITING_SETTLE_MS = 10_000

/**
 * How long a waiting cover stays over a video that has not painted yet. The
 * video is already allowed to show by then; a camera that never delivers a
 * frame must not stay covered for good.
 */
export const COVER_PAINT_CAP_MS = 1_500

/** The cover and its indicator fade out together over the painted video. */
export const COVER_REVEAL_MS = 200

/** Presented frames the cover waits for; see useCoverUntilPainted. */
export const REVEAL_AFTER_FRAMES = 2

/**
 * How long our own camera, with an effect chosen, may be on without a picture
 * and still count as starting. Its hold can end as LiveKit creates the sender,
 * before the track is published, and negotiating it can take a second or two.
 * A camera with no picture past this is not starting: it is dead (the device
 * went, or a restart failed) while still wanted on, and says so.
 */
export const EFFECT_START_GAP_MS = 3_000

export type WaitingIndicatorPhase = 'pulsing' | 'still' | 'problem'

/**
 * `covering`: the cover is fully up, over nothing or over a video that has not
 * painted yet. `revealing`: the video has painted and the cover fades out.
 */
export type CoverPhase = 'covering' | 'revealing'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.92); }
`

/**
 * Laid over the tile rather than in its flow, so the same cover stays put when
 * the video mounts beneath it, and after the video in the tile so it paints on
 * top of it. Our own camera's `<video>` carries the self-view mirror transform,
 * which makes it a stacking context painted at z-index 0; the z-index of 1 here
 * is what keeps the cover above it (and unmirrored: only the video is flipped,
 * never the tile). Under the tile's labels and name.
 */
const Cover = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  backgroundColor: VIDEO_TILE_COVER_BACKGROUND,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&[data-cover-phase="revealing"]': {
    animation: `${fadeOut} ${COVER_REVEAL_MS}ms ease-out forwards`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
  },
})

const Indicator = styled('span')({
  display: 'inline-flex',
  lineHeight: 0,
  color: CAMERA_WAITING_COLOR,
  '&[data-animation="fade-pulse"]': {
    animation: `${fadeIn} 100ms ease-out both, ${pulse} 1.4s ease-in-out 100ms infinite`,
  },
  '&[data-animation="fade"]': {
    animation: `${fadeIn} 100ms ease-out both`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
  },
})

/** Holds a tile's video; the waiting cover, when there is one, lies over it. */
export const CameraVideoFrame = styled('div')({
  width: '100%',
  height: '100%',
})

/**
 * Keeps a waiting cover over the video that replaces it until that video has
 * presented its second frame (the first can be blank on Safari), then fades
 * the cover out. Only that transition: a tile that
 * mounts with video, or whose camera comes back from plain "off", shows it at
 * once, as before.
 *
 * Without it the tile flashes on every camera-on. A freshly mounted `<video>`
 * is opaque black until LiveKit has attached the track and a frame has arrived,
 * and the tile behind it is see-through, so lifting the cover as the video
 * mounts shows the map, then black, then the picture.
 *
 * This never decides *whether* the video shows — `showsPicture` alone does,
 * and the video mounts exactly when it says so. It only decides how long the
 * cover stays over a video that is already allowed.
 *
 * `track` re-arms the paint signal when the video changes source before it has
 * painted; `videoRef` goes on the `<video>`.
 */
export function useCoverUntilPainted(
  showsPicture: boolean,
  waiting: boolean,
  track: unknown
): { coverPhase: CoverPhase | null; videoRef: (element: HTMLVideoElement | null) => void } {
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true })
  const reducedMotionRef = useRef(reducedMotion)
  reducedMotionRef.current = reducedMotion

  const shown = showsPicture ? 'video' : waiting ? 'waiting' : 'other'
  const [last, setLast] = useState(shown)
  const [phase, setPhase] = useState<CoverPhase | null>(null)
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  // Derived during render, not in an effect: an effect would let the video
  // commit once with no cover over it, the very flash this exists to stop.
  if (shown !== last) {
    setLast(shown)
    setPhase(shown === 'video' && last === 'waiting' ? 'covering' : null)
    if (shown !== 'video') setVideo(null)
  }

  // LiveKit's VideoTrack hands its element over again on every render, with a
  // null in between; only a new element is news.
  const videoRef = useCallback((element: HTMLVideoElement | null) => {
    if (element) setVideo(element)
  }, [])

  const reveal = useCallback(() => {
    setPhase((current) =>
      current === 'covering' ? (reducedMotionRef.current ? null : 'revealing') : current
    )
  }, [])

  const covering = phase === 'covering'

  useEffect(() => {
    if (!covering || !video) return undefined
    // A video frame callback fires on a frame that has been composited, the
    // only signal that means "not black any more". `loadeddata` / `playing` are
    // the fallback where it is missing (Firefox): they fire on decode, not paint.
    // The second presented frame, not the first. Safari runs effects through
    // a canvas `captureStream`, whose first presented frame can be the blank
    // canvas: revealing on it showed black, then the picture. One frame
    // later costs a frame time anywhere else.
    return whenVideoPainted(
      video,
      { frames: REVEAL_AFTER_FRAMES, fallbackEvents: ['loadeddata', 'playing'] },
      reveal
    )
  }, [covering, video, track, reveal])

  // Counted from the handover, not re-armed with the paint signal: the cap is
  // how long anyone looks at a cover over an allowed video.
  useEffect(() => {
    if (!covering) return undefined
    const timer = setTimeout(reveal, COVER_PAINT_CAP_MS)
    return () => clearTimeout(timer)
  }, [covering, reveal])

  useEffect(() => {
    if (phase !== 'revealing') return undefined
    const timer = setTimeout(() => setPhase(null), COVER_REVEAL_MS)
    return () => clearTimeout(timer)
  }, [phase])

  return { coverPhase: showsPicture ? phase : null, videoRef }
}

/**
 * Whether our own camera, with an effect chosen, is in the gap between its
 * start and its picture: covered as waiting, not "Camera disabled" for a moment
 * in between. Opens as the tile mounts or the camera is turned on without a
 * picture, or as its hold ends before there is one; closes on the picture, on
 * a hold (which covers it anyway), on the camera or effect going, or after
 * {@link EFFECT_START_GAP_MS}.
 *
 * A picture that goes away later is not a start: the device went or a restart
 * failed, LiveKit muted the track, and nothing turns the camera off for us. That
 * reads "Camera disabled" at once rather than waiting for something that will
 * not come.
 */
export function useEffectStartGap({
  cameraOn,
  effectChosen,
  waiting,
  showsPicture,
}: {
  cameraOn: boolean
  effectChosen: boolean
  waiting: boolean
  showsPicture: boolean
}): boolean {
  const eligible = effectChosen && !waiting && !showsPicture
  // A count, not a flag: a gap that opens again restarts its bound.
  const [gap, setGap] = useState(() => (eligible ? 1 : 0))
  const [seen, setSeen] = useState({ cameraOn, waiting })
  // Derived during render, like the cover itself: an effect would commit
  // "Camera disabled" once before the cover came back.
  const opens = (cameraOn && !seen.cameraOn) || (seen.waiting && !waiting)
  if (cameraOn !== seen.cameraOn || waiting !== seen.waiting) setSeen({ cameraOn, waiting })
  if (!eligible) {
    if (gap !== 0) setGap(0)
  } else if (opens) {
    setGap(gap + 1)
  }

  useEffect(() => {
    if (gap === 0) return undefined
    const timer = setTimeout(() => setGap(0), EFFECT_START_GAP_MS)
    return () => clearTimeout(timer)
  }, [gap])

  return eligible && (gap !== 0 || opens)
}

interface CameraWaitingPlaceholderProps {
  participantId: string
  /** Ours only: a problem that keeps the camera down. Remote tiles never know. */
  problem?: BackgroundProblem | null
  /** Someone else's tile: stops pulsing after {@link REMOTE_WAITING_SETTLE_MS}. */
  settles?: boolean
  /** `revealing` once the video beneath has painted (see {@link useCoverUntilPainted}). */
  phase?: CoverPhase
  iconSize: number
}

/**
 * The cover over a tile whose camera waits for its background: the same dark
 * box as any camera without a picture, with no words. It is the privacy cover
 * and is there from the first render, and so is its amber indicator: the camera
 * is on, and the tile says so at once.
 */
export function CameraWaitingPlaceholder({
  participantId,
  problem = null,
  settles = false,
  phase: coverPhase = 'covering',
  iconSize,
}: CameraWaitingPlaceholderProps) {
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true })
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    if (!settles) return undefined
    const timer = setTimeout(() => setSettled(true), REMOTE_WAITING_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [settles])

  // A failure is still: nothing pulses for something that will not resolve itself.
  const phase: WaitingIndicatorPhase = problem
    ? 'problem'
    : reducedMotion || settled
      ? 'still'
      : 'pulsing'
  const animation = reducedMotion
    ? 'none'
    : phase === 'pulsing'
      ? 'fade-pulse'
      : phase === 'problem'
        ? 'fade'
        : 'none'
  const label = cameraWaitingLabel(problem)

  return (
    <Cover
      data-testid={`camera-placeholder-${participantId}`}
      data-camera-waiting="true"
      data-waiting-indicator={phase}
      data-cover-phase={coverPhase}
      role="status"
      aria-label={label}
      title={label}
    >
      <Indicator
        key={phase === 'problem' ? 'problem' : 'camera'}
        data-testid={`camera-waiting-indicator-${participantId}`}
        data-animation={animation}
        aria-hidden
      >
        {phase === 'problem' ? (
          <WarningAmberRounded sx={{ fontSize: iconSize }} />
        ) : (
          <BsFillCameraVideoFill size={iconSize} />
        )}
      </Indicator>
    </Cover>
  )
}
